import { useState } from 'react';
import {
  MdAutoAwesome,
  MdWarning,
  MdExpandMore,
  MdExpandLess,
  MdError,
  MdInfo,
  MdTune,
  MdLock,
  MdBlock
} from 'react-icons/md';
import { getRiskStyles, RiskBadge } from './';

/**
 * RiskAssessmentCard - Unified risk assessment display
 *
 * Brings together score generation and display in one component.
 * Adapts UI based on current state (no score, scoring, scored).
 */
const RiskAssessmentCard = ({
  scoreResult,
  hasValidRecords,
  loading,
  onGenerateScore,
  onToggleFilters,
  mvrDaysAgo,
  breakdown,
  error,
}) => {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const riskStyles = scoreResult ? getRiskStyles(scoreResult.risk_level) : null;
  const hasScore = !!scoreResult;
  const insufficientHistory = !hasValidRecords && !hasScore;

  return (
    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-neutral-100 bg-gradient-to-r from-neutral-50 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-brand-100 rounded-full">
              <MdAutoAwesome className="text-brand-600" size={14} />
            </div>
            <span className="text-sm font-semibold text-neutral-800">
              AI Risk Assessment
            </span>
            {hasScore && (
              <div className="flex items-center gap-1 text-[10px] text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-full">
                <MdLock size={10} />
                <span>Locked</span>
              </div>
            )}
          </div>
          {onToggleFilters && (
            <button
              onClick={onToggleFilters}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-md transition-colors"
            >
              <MdTune size={14} />
              <span>Scoring Rules</span>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {hasScore ? (
          /* ═══════════════════════════════════════════
             SCORED STATE - Show results prominently
             ═══════════════════════════════════════════ */
          <div className="space-y-4">
            {/* Score Display Row */}
            <div className="flex items-center gap-5">
              {/* Large Score Circle with glow effect */}
              <div className="relative">
                <div className={`absolute inset-0 ${riskStyles.bg} opacity-20 rounded-full blur-md`} />
                <div className={`relative h-24 w-24 rounded-full ${riskStyles.bgMedium} border-4 border-white shadow-lg flex items-center justify-center`}>
                  <div className="text-center">
                    <span className="text-3xl font-bold text-neutral-800">
                      {scoreResult.score}
                    </span>
                    <div className="text-[9px] text-neutral-500 -mt-1">points</div>
                  </div>
                </div>
              </div>

              {/* Risk Info */}
              <div className="flex-1 space-y-2">
                <RiskBadge level={scoreResult.risk_level} size="md" dot />

                <p className="text-xs text-neutral-600">
                  Based on driving history analysis and configured scoring rules.
                </p>

                {/* MVR Age Indicator - show actual date */}
                {mvrDaysAgo !== null && mvrDaysAgo >= 0 && (
                  <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full ${
                    mvrDaysAgo > 90 ? 'bg-risk-high-50 text-risk-high-700 border border-risk-high-200' :
                    mvrDaysAgo > 30 ? 'bg-risk-medium-50 text-risk-medium-600 border border-risk-medium-200' :
                    'bg-risk-low-50 text-risk-low-700 border border-risk-low-200'
                  }`}>
                    MVR {mvrDaysAgo === 0 ? 'from today' :
                        mvrDaysAgo === 1 ? 'from yesterday' :
                        mvrDaysAgo > 365 ? `${Math.floor(mvrDaysAgo/30)}mo old` :
                        mvrDaysAgo > 30 ? `${Math.floor(mvrDaysAgo/30)}mo old` :
                        `${mvrDaysAgo}d old`}
                  </span>
                )}

                {/* Generated date - with fallback for invalid dates */}
                {scoreResult.created_at && (
                  <p className="text-[10px] text-neutral-400">
                    Generated: {(() => {
                      const date = new Date(scoreResult.created_at);
                      return isNaN(date.getTime())
                        ? scoreResult.created_at
                        : date.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          });
                    })()}
                  </p>
                )}
              </div>
            </div>

            {/* Score Breakdown Toggle */}
            {breakdown && (
              <>
                <button
                  onClick={() => setShowBreakdown(prev => !prev)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-xs font-medium rounded-lg border transition-all ${
                    showBreakdown
                      ? `${riskStyles.bgLight} ${riskStyles.border} ${riskStyles.text}`
                      : 'text-neutral-600 bg-neutral-50 hover:bg-neutral-100 border-neutral-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${riskStyles.bg}`} />
                    <span>Score Breakdown Details</span>
                  </div>
                  {showBreakdown ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
                </button>

                {/* Breakdown Content with visual connector */}
                {showBreakdown && (
                  <div className="relative">
                    {/* Connector line */}
                    <div className="absolute left-6 -top-2 w-0.5 h-2 bg-neutral-200" />

                    <div className={`p-4 rounded-lg ${riskStyles.bgLight} border-2 ${riskStyles.border}`}>
                      {/* Breakdown Header */}
                      <div className={`mb-3 pb-2 border-b ${riskStyles.border}`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${riskStyles.bg}`} />
                          <h4 className={`text-sm font-semibold ${riskStyles.text}`}>
                            {scoreResult.risk_level} Risk - Score Analysis
                          </h4>
                        </div>
                      </div>
                      {breakdown}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Help Note */}
            <div className="flex items-start gap-2 p-3 bg-brand-50 rounded-lg border border-brand-100">
              <MdInfo className="text-brand-500 flex-shrink-0 mt-0.5" size={14} />
              <p className="text-[11px] text-brand-700">
                This score is locked for consistent evaluation. Need to test different scoring rules?
                Contact customer service for custom scoring options.
              </p>
            </div>
          </div>

        ) : insufficientHistory ? (
          /* ═══════════════════════════════════════════
             INSUFFICIENT HISTORY STATE
             ═══════════════════════════════════════════ */
          <div className="text-center py-6">
            <div className="h-20 w-20 mx-auto rounded-full bg-gradient-to-br from-risk-medium-50 to-risk-medium-100 flex items-center justify-center mb-4 shadow-inner">
              <MdWarning className="text-risk-medium-500" size={32} />
            </div>
            <p className="text-base font-medium text-neutral-700 mb-2">
              Insufficient Driving History
            </p>
            <p className="text-sm text-neutral-500 max-w-sm mx-auto leading-relaxed">
              There is not enough driving history available to generate a reliable risk score.
              A minimum driving history is required for accurate analysis.
            </p>
          </div>

        ) : (
          /* ═══════════════════════════════════════════
             READY TO GENERATE STATE
             ═══════════════════════════════════════════ */
          <div className="space-y-4">
            {/* Info Banner */}
            <div className="bg-gradient-to-br from-brand-50 to-blue-50 rounded-lg p-4 border border-brand-100">
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <div className="p-2 bg-white rounded-full shadow-sm">
                    <MdAutoAwesome className="text-brand-500" size={20} />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-brand-800 mb-1">
                    Generate Risk Score
                  </h4>
                  <p className="text-xs text-neutral-600 leading-relaxed">
                    Generate a one-time risk assessment score from this driver's current record.
                    Once generated, the score will be locked for consistent evaluation.
                  </p>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${
                error.includes('QUOTA') || error.includes('Quota') 
                  ? 'bg-yellow-50 border-yellow-300' 
                  : 'bg-risk-high-50 border-risk-high-200'
              }`}>
                {error.includes('QUOTA') || error.includes('Quota') ? (
                  <MdBlock className="text-yellow-600 flex-shrink-0 mt-0.5" size={16} />
                ) : (
                  <MdError className="text-risk-high-600 flex-shrink-0 mt-0.5" size={16} />
                )}
                <div className="flex-1">
                  <p className={`text-xs whitespace-pre-line ${
                    error.includes('QUOTA') || error.includes('Quota')
                      ? 'text-yellow-800'
                      : 'text-risk-high-700'
                  }`}>
                    {error}
                  </p>
                </div>
              </div>
            )}

            {/* Generate Button - More prominent */}
            <button
              onClick={onGenerateScore}
              disabled={loading}
              className="w-full py-4 px-6 rounded-xl text-sm font-semibold flex items-center justify-center gap-3
                bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800
                text-white shadow-lg shadow-brand-200
                transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full" />
                  <span>Analyzing Driver Record...</span>
                </>
              ) : (
                <>
                  <MdAutoAwesome size={20} />
                  <span>Generate Driver Score</span>
                </>
              )}
            </button>

          </div>
        )}
      </div>
    </div>
  );
};

export default RiskAssessmentCard;
